import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Typography, TextField, FormControl, Radio, RadioGroup, FormControlLabel } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import { isNil } from "ramda";

import { useShares, useUsers, useAlgoliaEvents } from "hooks";
import MakePresenter from "presenters/MakePresenter";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import { getTomorrow, isToday } from "utils/dateTimeUtils";
import { SCHEDULED_FOR_MAILING } from "enums";
import { initialValues, validationSchema, attributesToSubmit, LIMIT_EMAIL_SHARE } from "forms/ShareEmailForm";
import { trackShare } from "tracking/google/analytics";
import { SHARE_METHODS } from "enums";

import Success from "./components/Success";
import SuccessScheduled from "./components/SuccessScheduled";
import useStyles from "./useStyles";

const getTextFieldProps = () => {
  return {
    InputLabelProps: {
      shrink: true,
    },
    margin: "normal",
  };
};

const EmailForm = (props) => {
  const classes = useStyles();
  const { make, setShowCVPModalNext, onSubmit, isPending, isFulfilled, templateGroup } = props;
  const { dateToShare } = useShares();
  const { currentUser } = useUsers();
  const { trackEventAfterSearchAndShare } = useAlgoliaEvents();

  const getInitialScheduleForMailing = () =>
    isToday(dateToShare) ? SCHEDULED_FOR_MAILING.now : SCHEDULED_FOR_MAILING.later;

  const handleSubmitFunc = (values) => {
    onSubmit(attributesToSubmit(values, make));
  };

  useEffect(() => {
    if (isFulfilled) {
      trackShare(SHARE_METHODS.EMAIL, templateGroup);
      trackEventAfterSearchAndShare(make);
      setShowCVPModalNext(true);
    }
  }, [isFulfilled]);

  const formik = useFormik({
    initialValues: initialValues(
      !isNil(currentUser.firstName) ? currentUser.firstName : "",
      getInitialScheduleForMailing(),
      dateToShare,
    ),
    validationSchema,
    validateOnBlur: false,
    onSubmit: handleSubmitFunc,
  });
  const { values, touched, errors, getFieldProps, handleSubmit, setFieldValue, isSubmitting } = formik;
  const [dateSelected, setDateSelected] = useState(values.scheduledAt);

  const handleDateChange = (value) => {
    setDateSelected(value);
    setFieldValue("scheduledAt", value);
  };

  if (isFulfilled) {
    return values.scheduledForMailing === SCHEDULED_FOR_MAILING.later ? <SuccessScheduled /> : <Success />;
  }

  return (
    <div className={classes.root} data-testid="emailform">
      <Typography variant="h2" align="center" classes={{ root: classes.title }}>
        Send Ecard
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          data-testid="textFieldFrom"
          label="From"
          placeholder="Your Name"
          {...getTextFieldProps()}
          {...getFieldProps("ownerName")}
          helperText={touched.ownerName && errors.ownerName}
          error={touched.ownerName && Boolean(errors.ownerName)}
        />

        <TextField
          data-testid="textFieldTo"
          label="To"
          multiline
          rows="5"
          {...getTextFieldProps()}
          {...getFieldProps("recipients")}
          placeholder="email@example.com, email2@example.com"
          helperText={
            touched.recipients && errors.recipients
              ? errors.recipients
              : `(use commas to send to multiple addresses, up to ${LIMIT_EMAIL_SHARE} emails at a time)`
          }
          inputProps={{ autoCorrect: "off", autoCapitalize: "none" }}
          error={touched.recipients && Boolean(errors.recipients)}
        />

        <FormControl fullWidth component="fieldset" classes={{ root: classes.formControl }}>
          <RadioGroup
            data-testid="radioGroupSend"
            row
            aria-label="sending time"
            name="scheduledAt"
            classes={{ root: classes.radioGroup }}
            {...getFieldProps("scheduledForMailing")}
          >
            <FormControlLabel value={SCHEDULED_FOR_MAILING.now} control={<Radio />} label="SEND NOW!" />
            <FormControlLabel
              value={SCHEDULED_FOR_MAILING.later}
              control={<Radio />}
              label="SEND LATER"
              onChange={() => handleDateChange(dateSelected)}
            />
          </RadioGroup>
        </FormControl>

        {values.scheduledForMailing === SCHEDULED_FOR_MAILING.now ? (
          <Typography align="center" component="p" className={classes.description}>
            Your card will be sent immediately
          </Typography>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Choose date"
              slotProps={{ textField: getTextFieldProps() }}
              name="scheduledAt"
              value={dateSelected}
              onChange={handleDateChange}
              animateYearScrolling
              helperText={touched.scheduledAt && errors.scheduledAt}
              error={touched.scheduledAt && Boolean(errors.scheduledAt)}
              disablePast
              minDate={getTomorrow()}
            />
          </LocalizationProvider>
        )}

        <div className={classes.actions}>
          <LoadingButton
            loading={isSubmitting}
            disabled={isPending}
            aria-label="submit"
            type="submit"
            size="large"
            variant="contained"
            color="primary"
          >
            {values.scheduledForMailing === SCHEDULED_FOR_MAILING.now ? "SEND CARD" : "SCHEDULE CARD NOW!"}
          </LoadingButton>
        </div>
      </form>
    </div>
  );
};

EmailForm.propTypes = {
  make: MakePresenter.shape(),
  setShowCVPModalNext: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  isPending: PropTypes.bool,
  isFulfilled: PropTypes.bool,
  templateGroup: TemplateGroupPresenter.shape(),
};

EmailForm.defaultProps = {
  isPending: false,
  isFulfilled: false,
};

export default EmailForm;
